"use client";
import { DatePicker, Input, SelectInput } from "@/components";
import { MultiSelectField } from "@/components/ui/forms/MultiSelectField";
import { useSkills } from "@/hooks";
import { OptionsJobber } from "@/types";
import { useEffect, useState, useMemo } from "react";
import { useFormContext } from "react-hook-form";
import { LuChevronDown, LuChevronUp, LuTrash2, LuTriangleAlert, LuInfo } from "react-icons/lu";
import { ReferenceForm } from "./ReferenceForm";
import { NoReferenceWarningModal } from "@/components/ui/NoReferenceWarningModal";
import { SessionStorage, SESSION_STORAGE_KEYS } from "@/utils/sessionStorage";
import { useTranslations } from "next-intl";

// Simple confirmation modal component
const ConfirmDeleteModal = ({
  isOpen,
  onConfirm,
  onCancel,
  title,
  deleteType,
}: {
  isOpen: boolean;
  onConfirm: () => void;
  onCancel: () => void;
  title: string;
  deleteType: 'deleteExperience' | 'deleteEducation';
}) => {
  const tCommon = useTranslations("jobberProfile.forms.stepThree.common");
  const tDeleteModal = useTranslations("jobberProfile.forms.stepThree.deleteModal");
  
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/30 backdrop-blur-sm flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 max-w-sm w-full mx-4">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">
          {tDeleteModal(deleteType)}
        </h3>
        <p className="text-gray-600 mb-6">
          {tDeleteModal('confirmMessage', {title})}
        </p>
        <div className="flex gap-3 justify-end">
          <button
            type="button"
            onClick={onCancel}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
          >
            {tCommon("cancel")}
          </button>
          <button
            type="button"
            onClick={onConfirm}
            className="px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-lg transition-colors"
          >
            {tCommon("delete")}
          </button>
        </div>
      </div>
    </div>
  );
};

type EducationFormProp = {
  index: number;
  optionsJobber: {
    options?: OptionsJobber;
    optionError: boolean;
    optionIsLoading: boolean;
  };
  isCollapsed: boolean;
  shouldShowCollapse: boolean;
  hasData: boolean;
  onToggleCollapse: () => void;
  onRemove: () => void;
  isComplete: boolean;
  isConfirmed: boolean;
  onConfirm: () => void;
  onEdit: () => void;
  validateSignal?: number;
  confirmReminderSignal?: number;
  highlightUnconfirmed?: boolean;
};

export const EducationForm = ({
  index,
  optionsJobber,
  isCollapsed,
  shouldShowCollapse,
  hasData,
  onToggleCollapse,
  onRemove,
  isComplete,
  isConfirmed,
  onConfirm,
  onEdit,
  validateSignal,
  confirmReminderSignal,
  highlightUnconfirmed,
}: EducationFormProp) => {
  const { watch, setValue } = useFormContext();
  const [searchNameSkill, setSearchNameSkill] = useState("");
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showNoReferenceWarning, setShowNoReferenceWarning] = useState(false);
  const [isEditing, setIsEditing] = useState(!isConfirmed);
  const [needsConfirmationHighlight, setNeedsConfirmationHighlight] = useState(false);
  
  const tCommon = useTranslations("jobberProfile.forms.stepThree.common");
  const tDeleteModal = useTranslations("jobberProfile.forms.stepThree.deleteModal");
  const tEducation = useTranslations("jobberProfile.forms.stepThree.educationForm");

  // Watch the selected category to filter skills
  const selectedCategory = watch(`educations.${index}.category`);
  const categoryId = selectedCategory?.id || "";

  const {
    data: skillsData,
    fetchNextPage: fetchNextSkillsPage,
    hasNextPage: hasNextSkillsPage,
    isFetchingNextPage: isFetchingNextSkillsPage,
    isLoading: isLoadingSkills,
  } = useSkills(categoryId, searchNameSkill);

  const certificationTitle =
    watch(`educations.${index}.certification_title`) || tEducation("untitledEducation");

  const referenceData = watch(`educations.${index}.reference`);
  
  // Watch and normalize skills
  const watchedSkills = watch(`educations.${index}.skills_education`);

  // Normalize skills to ensure {id, name} structure
  const normalizedSkills = useMemo(() => {
    if (!Array.isArray(watchedSkills)) return [];
    return watchedSkills.map((skill: any) => {
      // If skill already has {id, name}, return as is
      if (skill?.id && skill?.name) return skill;
      // Transform {first_id, name_first} to {id, name}
      if (skill?.first_id && skill?.name_first) {
        return { id: skill.first_id, name: skill.name_first };
      }
      return skill;
    });
  }, [watchedSkills, index]);

  // Sync normalized skills back to form if they were transformed
  useEffect(() => {
    if (normalizedSkills.length > 0 && watchedSkills.length > 0) {
      const firstWatched = watchedSkills[0];
      const firstNormalized = normalizedSkills[0];
      // Only update if transformation occurred
      if (firstWatched?.first_id && !firstWatched?.id && firstNormalized?.id) {
        setValue(`educations.${index}.skills_education`, normalizedSkills, { shouldValidate: true });
      }
    }
  }, [normalizedSkills, watchedSkills, index, setValue]);

  const hasReference = !!referenceData;
  const referenceIncomplete = !!(referenceData && (
    !referenceData.first_name || !referenceData.last_name || !referenceData.relationship || !referenceData.email
  ));

  // Status respects partial reference
  const getValidationStatus = () => {
    if (isConfirmed) return 'confirmed';
    if (isComplete && !referenceIncomplete) return 'verify';
    return 'incomplete';
  };

  const validationStatus = getValidationStatus();

  // Conditional reference requirement: only after any field started
  const refStarted = !!(referenceData && (
    referenceData.first_name || referenceData.last_name || referenceData.relationship || referenceData.email
  ));

  const certificationTitleValue = watch(`educations.${index}.certification_title`);
  const certificationTypeValue = watch(`educations.${index}.certification_type`);
  const institutionNameValue = watch(`educations.${index}.institution_name`);
  const categoryValue = watch(`educations.${index}.category`);
  const startDateValue = watch(`educations.${index}.start_date`);
  const skillsValue = normalizedSkills || [];

  const normaliseOptionFilled = (value: unknown) => {
    if (!value) return false;
    if (typeof value === "string") return value.trim().length > 0;
    if (typeof value === "object") {
      const option = value as { id?: string | number; value?: string | number };
      return Boolean(option?.id ?? option?.value);
    }
    return false;
  };

  const missingKeys: string[] = [];
  if (!certificationTitleValue || !(`${certificationTitleValue}`.trim())) missingKeys.push("certification_title");
  if (!normaliseOptionFilled(certificationTypeValue)) missingKeys.push("certification_type");
  if (!institutionNameValue || !(`${institutionNameValue}`.trim())) missingKeys.push("institution_name");
  if (!normaliseOptionFilled(categoryValue)) missingKeys.push("category");
  if (!startDateValue) missingKeys.push("start_date");
  if (!Array.isArray(skillsValue) || skillsValue.length === 0) missingKeys.push("skills_education");

  if (referenceData) {
    if (!referenceData.first_name) missingKeys.push("reference_first_name");
    if (!referenceData.last_name) missingKeys.push("reference_last_name");
    if (!referenceData.relationship) missingKeys.push("reference_relationship");
    if (!referenceData.email) missingKeys.push("reference_email");
  }

  const missingSet = new Set(missingKeys);
  const [attemptedConfirm, setAttemptedConfirm] = useState(0);
  const [localAttemptedConfirm, setLocalAttemptedConfirm] = useState(0); // Solo para el botón Confirm local
  const [flashActive, setFlashActive] = useState(false);
  const [flashCount, setFlashCount] = useState(0);
  const needsAttention = localAttemptedConfirm > 0 && missingSet.size > 0; // Solo con intento local
  const hasPartialData = missingSet.size > 0;
  const showConfirmReminder = highlightUnconfirmed && !isConfirmed && missingSet.size === 0;
  const emphasizeBorder = needsAttention; // Solo enfatizar cuando haya intentado confirmar localmente y falten campos
  useEffect(() => {
    if (typeof validateSignal === 'number') {
      setAttemptedConfirm(c => c + 1);
    }
  }, [validateSignal]);
  useEffect(() => {
    if (!showConfirmReminder) {
      setNeedsConfirmationHighlight(false);
      return;
    }
    setNeedsConfirmationHighlight(true);
    const timeout = setTimeout(() => setNeedsConfirmationHighlight(false), 2000);
    return () => clearTimeout(timeout);
  }, [showConfirmReminder, confirmReminderSignal]);
  useEffect(() => {
    if (!flashActive) return;
    if (flashCount >= 3) {
      const t = setTimeout(() => setFlashActive(false), 150);
      return () => clearTimeout(t);
    }
    const t = setTimeout(() => setFlashCount(c => c + 1), 1200);
    return () => clearTimeout(t);
  }, [flashActive, flashCount]);
  const flashClass = (key: string) => (needsAttention && flashActive && missingSet.has(key)) ? 'flash-border-3x' : '';

  const handleConfirm = () => {
    // First check if there are missing required fields
    if (missingSet.size > 0) {
      // Increment localAttemptedConfirm to show validation errors
      setLocalAttemptedConfirm(c => c + 1);
      setFlashActive(true);
      setFlashCount(0);
      return;
    }

    // Check if warning has already been shown this session
    const warningShown = SessionStorage.getItem(SESSION_STORAGE_KEYS.NO_REFERENCE_WARNING_SHOWN);

    // If no reference and warning hasn't been shown, show warning
    if (!hasReference && !warningShown) {
      setShowNoReferenceWarning(true);
      SessionStorage.setItem(SESSION_STORAGE_KEYS.NO_REFERENCE_WARNING_SHOWN, 'true');
      return;
    }

    // Set editing to false and proceed with confirm
    setIsEditing(false);
    onConfirm();
  };

  const handleEdit = () => {
    setIsEditing(true);
    onEdit();
  };

  const handleWarningClose = () => {
    setShowNoReferenceWarning(false);
    // Set editing to false and proceed with confirm after user acknowledges warning
    setIsEditing(false);
    onConfirm();
  };

  return (
    <div
      className={`mb-4 transition-all duration-300 ${needsConfirmationHighlight ? "shadow-[0_0_0_3px_rgba(239,68,68,0.35)] rounded-lg" : ""
        }`}
    >
      {shouldShowCollapse && hasData && (
        <>
          {/* Desktop Layout - Single row */}
          <div
            className={`hidden md:flex items-center justify-between p-3 rounded-t-lg border ${emphasizeBorder
              ? 'bg-red-50 border-red-500 border-2 shadow-sm'
              : 'bg-gray-50 border-gray-200'
              }`}
          >
            <div
              className="flex items-center gap-2 flex-1 cursor-pointer hover:bg-gray-100 -m-3 p-3 rounded-tl-lg mr-2"
              onClick={onToggleCollapse}
            >
              <button
                type="button"
                className="p-1 hover:bg-gray-200 rounded cursor-pointer"
              >
                {isCollapsed ? (
                  <LuChevronDown size={16} />
                ) : (
                  <LuChevronUp size={16} />
                )}
              </button>
              <span className="font-medium text-gray-700">
                {certificationTitle}
              </span>
            </div>
            <div className="flex items-center gap-3">
              {!hasReference && (
                <span className="flex items-center gap-1 text-yellow-600 text-xs bg-yellow-100 px-2 py-1 rounded-full">
                  <LuTriangleAlert size={12} />
                  {tCommon("noReference")}
                </span>
              )}
              <div className="w-20 flex justify-center">
                {validationStatus === 'incomplete' && (
                  <span className="text-red-600 text-xs bg-red-100 px-2 py-1 rounded-full">
                    {tCommon("incomplete")}
                  </span>
                )}
                {validationStatus === 'verify' && (
                  <span className="text-orange-600 text-xs bg-orange-100 px-2 py-1 rounded-full">
                    {tCommon("verify")}
                  </span>
                )}
                {validationStatus === 'confirmed' && (
                  <span className="text-green-600 text-xs bg-green-100 px-2 py-1 rounded-full">
                    {tCommon("confirmed")}
                  </span>
                )}
              </div>
              <button
                type="button"
                onClick={() => setShowDeleteModal(true)}
                className="p-1 hover:bg-red-100 rounded text-red-600"
                title={tCommon("removeEducation")}
              >
                <LuTrash2 size={16} />
              </button>
            </div>
          </div>

          {/* Mobile Layout - Two rows */}
          <div
            className={`md:hidden rounded-t-lg border ${emphasizeBorder
              ? 'bg-red-50 border-red-500 border-2 shadow-sm'
              : 'bg-gray-50 border-gray-200'
              }`}
          >
            {/* First row - Title and controls */}
            <div className="flex items-center justify-between p-3">
              <div
                className="flex items-center gap-2 flex-1 cursor-pointer hover:bg-gray-100 -m-3 p-3 rounded-tl-lg mr-2"
                onClick={onToggleCollapse}
              >
                <button
                  type="button"
                  className="p-1 hover:bg-gray-200 rounded cursor-pointer"
                >
                  {isCollapsed ? (
                    <LuChevronDown size={16} />
                  ) : (
                    <LuChevronUp size={16} />
                  )}
                </button>
                <span className="font-medium text-gray-700">
                  {certificationTitle}
                </span>
              </div>
              <button
                type="button"
                onClick={() => setShowDeleteModal(true)}
                className="p-1 hover:bg-red-100 rounded text-red-600"
                title={tCommon("removeEducation")}
              >
                <LuTrash2 size={16} />
              </button>
            </div>

            {/* Second row - Status pills */}
            <div className="flex items-center gap-3 px-3 pb-3">
              {!hasReference && (
                <span className="flex items-center gap-1 text-yellow-600 text-xs bg-yellow-100 px-2 py-1 rounded-full">
                  <LuTriangleAlert size={12} />
                  {tCommon("noReference")}
                </span>
              )}
              <div className="flex justify-start">
                {validationStatus === 'incomplete' && (
                  <span className="text-red-600 text-xs bg-red-100 px-2 py-1 rounded-full">
                    {tCommon("incomplete")}
                  </span>
                )}
                {validationStatus === 'verify' && (
                  <span className="text-orange-600 text-xs bg-orange-100 px-2 py-1 rounded-full">
                    {tCommon("verify")}
                  </span>
                )}
                {validationStatus === 'confirmed' && (
                  <span className="text-green-600 text-xs bg-green-100 px-2 py-1 rounded-full">
                    {tCommon("confirmed")}
                  </span>
                )}
              </div>
            </div>
          </div>
        </>
      )}

      <ConfirmDeleteModal
        isOpen={showDeleteModal}
        onConfirm={() => {
          onRemove();
          setShowDeleteModal(false);
        }}
        onCancel={() => setShowDeleteModal(false)}
        title={certificationTitle}
        deleteType="deleteEducation"
      />

      <div
        className={`${shouldShowCollapse && hasData
          ? (emphasizeBorder ? 'border-2 border-red-500 border-t-0 rounded-b-lg bg-red-50/30 shadow-lg' : 'border border-gray-200 border-t-0 rounded-b-lg')
          : (emphasizeBorder ? 'border-2 border-red-500 rounded-lg bg-red-50/30 shadow-lg' : '')
          } ${isCollapsed && shouldShowCollapse && hasData ? 'hidden' : 'block'}`}
      >
        {/* Validation status pill for expanded view */}
        {!shouldShowCollapse || !hasData ? (
          <div className="flex items-center gap-2 p-2 border-b border-gray-200">
            <span className="font-medium text-gray-700">{certificationTitle}</span>
            <div className="w-20 flex justify-center">
              {validationStatus === 'incomplete' && (
                <span className="text-red-600 text-xs bg-red-100 px-2 py-1 rounded-full">
                  Incomplete
                </span>
              )}
              {validationStatus === 'verify' && (
                <span className="text-orange-600 text-xs bg-orange-100 px-2 py-1 rounded-full">
                  Verify
                </span>
              )}
              {validationStatus === 'confirmed' && (
                <span className="text-green-600 text-xs bg-green-100 px-2 py-1 rounded-full">
                  Complete
                </span>
              )}
            </div>
          </div>
        ) : null}

        {/* Error banner when validation fails */}
        {needsAttention && (
          <div className="bg-red-100 border-l-4 border-red-500 p-4 m-4 mb-0 rounded-r-md">
            <div className="flex items-center">
              <LuTriangleAlert className="text-red-500 mr-2" size={16} />
              <p className="text-red-700 text-sm font-medium">
                {tCommon("completeAllFields")}
              </p>
            </div>
            <p className="text-red-600 text-xs mt-1 ml-6">
              {tEducation("requiredFields")}
            </p>
          </div>
        )}

        <div className="p-4 space-y-4">
          {/* Certification Information */}
          <div className="bg-white border border-[#EDEEFE] rounded-2xl p-4">
            <h3 className="font-normal text-base sm:text-sm mb-4 flex items-center gap-2">
              <svg xmlns="http://www.w3.org/2000/svg" width="17" height="16" viewBox="0 0 17 16" fill="none">
              <path d="M12.6952 12.1074L12.3901 11.5907V11.5907L12.6952 12.1074ZM13.0493 11.487H12.4493V11.487H13.0493ZM7.30462 14.3767L6.98275 14.883V14.883L7.30462 14.3767ZM8.81032 14.4014L9.1154 14.918V14.918L8.81032 14.4014ZM0.775922 4.81817L1.08308 5.33359L0.775922 4.81817ZM0.775923 5.43709L0.468766 5.95251H0.468766L0.775923 5.43709ZM8.41873 9.55212L8.11158 9.03671V9.03671L8.41873 9.55212ZM7.68106 9.55212L7.98821 9.03671L7.68106 9.55212ZM15.3239 4.81817L15.631 4.30275V4.30275L15.3239 4.81817ZM15.3239 5.43709L15.631 5.95251L15.3239 5.43709ZM3.73964 12.1106L4.06151 11.6042V11.6042L3.73964 12.1106ZM8.41873 0.703131L8.11158 1.21855V1.21855L8.41873 0.703131ZM7.68106 0.703131L7.98821 1.21855V1.21855L7.68106 0.703131ZM15.9989 5.11564C15.9989 4.78427 15.7302 4.51564 15.3989 4.51564C15.0675 4.51564 14.7989 4.78427 14.7989 5.11564H15.3989H15.9989ZM14.7989 9.00627C14.7989 9.33764 15.0675 9.60627 15.3989 9.60627C15.7302 9.60627 15.9989 9.33764 15.9989 9.00627H15.3989H14.7989ZM8.41873 0.703131L8.11158 1.21855L15.0167 5.33359L15.3239 4.81817L15.631 4.30275L8.72589 0.187714L8.41873 0.703131ZM15.3239 5.43709L15.0167 4.92167L8.11158 9.03671L8.41873 9.55212L8.72589 10.0675L15.631 5.95251L15.3239 5.43709ZM0.775922 4.81817L1.08308 5.33359L7.98821 1.21855L7.68106 0.703131L7.3739 0.187714L0.468765 4.30275L0.775922 4.81817ZM7.68106 9.55212L7.98821 9.03671L3.71281 6.48883L3.40565 7.00425L3.0985 7.51967L7.3739 10.0675L7.68106 9.55212ZM3.40565 7.00425L3.71281 6.48883L1.08308 4.92167L0.775923 5.43709L0.468766 5.95251L3.0985 7.51967L3.40565 7.00425ZM3.40565 7.00425H2.80566V11.5025H3.40565H4.00566V7.00425H3.40565ZM3.73964 12.1106L3.41777 12.6169L6.98275 14.883L7.30462 14.3767L7.62649 13.8703L4.06151 11.6042L3.73964 12.1106ZM8.81032 14.4014L9.1154 14.918L13.0002 12.624L12.6952 12.1074L12.3901 11.5907L8.50524 13.8847L8.81032 14.4014ZM13.0493 11.487H13.6493L13.6493 7.00425H13.0493L12.4493 7.00425L12.4493 11.487H13.0493ZM12.6952 12.1074L13.0002 12.624C13.4025 12.3865 13.6493 11.9541 13.6493 11.487H13.0493H12.4493C12.4493 11.5296 12.4268 11.5691 12.3901 11.5907L12.6952 12.1074ZM7.30462 14.3767L6.98275 14.883C7.6305 15.2948 8.45448 15.3083 9.1154 14.918L8.81032 14.4014L8.50524 13.8847C8.23291 14.0455 7.89339 14.04 7.62649 13.8703L7.30462 14.3767ZM0.775922 4.81817L0.468765 4.30275C-0.156127 4.67515 -0.156123 5.58011 0.468766 5.95251L0.775923 5.43709L1.08308 4.92167C1.2391 5.01465 1.2391 5.24061 1.08308 5.33359L0.775922 4.81817ZM8.41873 9.55212L8.11158 9.03671C8.07357 9.05936 8.02622 9.05936 7.98821 9.03671L7.68106 9.55212L7.3739 10.0675C7.79039 10.3157 8.3094 10.3157 8.72589 10.0675L8.41873 9.55212ZM15.3239 4.81817L15.0167 5.33359C14.8607 5.24061 14.8607 5.01465 15.0167 4.92167L15.3239 5.43709L15.631 5.95251C16.2559 5.58011 16.2559 4.67515 15.631 4.30275L15.3239 4.81817ZM3.40565 11.5025H2.80566C2.80566 11.9542 3.03655 12.3746 3.41777 12.6169L3.73964 12.1106L4.06151 11.6042C4.02672 11.5821 4.00566 11.5437 4.00566 11.5025H3.40565ZM8.41873 0.703131L8.72589 0.187714C8.3094 -0.0604875 7.79039 -0.0604886 7.3739 0.187714L7.68106 0.703131L7.98821 1.21855C8.02622 1.1959 8.07357 1.1959 8.11158 1.21855L8.41873 0.703131ZM15.3989 5.11564H14.7989V9.00627H15.3989H15.9989V5.11564H15.3989Z" fill="#151515"/>
              </svg>
              {tEducation("certification")}
            </h3>
            <div className="space-y-4">
              <Input
                name={`educations.${index}.certification_title`}
                label={tEducation("certificationTitle")}
                placeholder={tEducation("certificationTitlePlaceholder")}
                disabled={isConfirmed && !isEditing}
                className={`bg-[#F9F9F9] ${flashClass('certification_title')}`}
                noBorder
                noShadow
                required
              />
              <SelectInput
                name={`educations.${index}.certification_type`}
                options={optionsJobber.options?.type_certification || []}
                loading={false}
                label={tEducation("certificationType")}
                placeholder={tEducation("certificationTypePlaceholder")}
                disabled={isConfirmed && !isEditing}
                className={`bg-[#F9F9F9] ${flashClass('certification_type')}`}
                noBorder
                noShadow
                required
              />
            </div>
          </div>

          {/* Institution Information */}
          <div className="bg-white border border-[#EDEEFE] rounded-2xl p-4">
            <h3 className="font-normal text-base sm:text-sm mb-4 flex items-center gap-2">
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="15" viewBox="0 0 16 15" fill="none">
                <path d="M8.54442 6.37934H12.0111C12.8201 6.37934 13.2245 6.37934 13.5335 6.53678C13.8053 6.67526 14.0263 6.89623 14.1648 7.16802C14.3222 7.477 14.3222 7.88149 14.3222 8.69045V13.6016M8.54442 6.37934V2.91267C8.54442 2.10371 8.54442 1.69923 8.38699 1.39024C8.2485 1.11845 8.02753 0.897481 7.75574 0.758998C7.44676 0.601562 7.04227 0.601562 6.23331 0.601562H3.63331C2.82434 0.601562 2.41986 0.601562 2.11088 0.758998C1.83909 0.897481 1.61812 1.11845 1.47963 1.39024C1.3222 1.69923 1.3222 2.10371 1.3222 2.91267V13.6016M8.54442 6.37934V13.6016M14.3222 13.6016H15.0444M14.3222 13.6016H8.54442M8.54442 13.6016H1.3222M1.3222 13.6016H0.599976M3.84998 3.49045H6.01664M3.84998 6.37934H6.01664M3.84998 9.26823H6.01664" stroke="#151515" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              {tEducation("institutionInformation")}
            </h3>
            <div className="space-y-4">
              <Input
                name={`educations.${index}.institution_name`}
                label={tEducation("issuingInstitution")}
                placeholder={tEducation("institutionNamePlaceholder")}
                disabled={isConfirmed && !isEditing}
                className={`bg-[#F9F9F9] ${flashClass('institution_name')}`}
                noBorder
                noShadow
                required
              />
              <SelectInput
                name={`educations.${index}.category`}
                options={optionsJobber.options?.category || []}
                loading={false}
                label={tEducation("category")}
                placeholder={tEducation("categoryPlaceholder")}
                disabled={isConfirmed && !isEditing}
                className={`bg-[#F9F9F9] ${flashClass('category')}`}
                noBorder
                noShadow
                required
              />
            </div>
          </div>

          {/* Date Information */}
          <div className="bg-white border border-[#EDEEFE] rounded-2xl p-4">
            <h3 className="font-normal text-base sm:text-sm mb-4 flex items-center gap-2">
              <svg xmlns="http://www.w3.org/2000/svg" width="12" height="14" viewBox="0 0 12 14" fill="none">
                <path d="M1.26664 4.5444H10.6M2.47299 0.601562V1.63025M9.26664 0.601562V1.63013M11.2666 3.43013V10.8016C11.2666 11.7957 10.4707 12.6016 9.48886 12.6016H2.37775C1.39591 12.6016 0.599976 11.7957 0.599976 10.8016V3.43013C0.599976 2.43602 1.39591 1.63013 2.37775 1.63013H9.48886C10.4707 1.63013 11.2666 2.43602 11.2666 3.43013Z" stroke="#151515" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              {tEducation("dateInformation")}
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className={flashClass('start_date')}>
                <DatePicker
                  name={`educations.${index}.start_date`}
                  label={tEducation("startDate")}
                  placeholder={tEducation("startDatePlaceholder")}
                  disabled={isConfirmed && !isEditing}
                  rules={{
                    required: tEducation("startDateRequired"),
                  }}
                  noBorder
                  noShadow
                  className="bg-[#F9F9F9]"
                  required
                />
              </div>
              <DatePicker
                name={`educations.${index}.end_date`}
                label={tEducation("endDate")}
                placeholder={tEducation("endDatePlaceholder")}
                disabled={isConfirmed && !isEditing}
                minDate={
                  watch(`educations.${index}.start_date`) &&
                    watch(`educations.${index}.start_date`) !== "" ?
                    new Date(watch(`educations.${index}.start_date`)!) : undefined
                }
                isRangeValidation={true}
                noBorder
                noShadow
                className="bg-[#F9F9F9]"
              />
            </div>
          </div>

          {/* Skills */}
          <div className="bg-white border border-[#EDEEFE] rounded-2xl p-4">
            <h3 className="font-normal text-base sm:text-sm mb-4 flex items-center gap-2">
              <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 14 14" fill="none">
                <path d="M8.90002 5.14841L5.544 8.14839L4.40002 7.12578M12.65 2.89842L12.65 10.3985C12.65 11.6411 11.6427 12.6484 10.4 12.6484H2.90002C1.65738 12.6484 0.650024 11.6411 0.650024 10.3985V2.89842C0.650024 1.65579 1.65738 0.648438 2.90002 0.648438H10.4C11.6427 0.648438 12.65 1.65579 12.65 2.89842Z" stroke="#151515" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              {tEducation("skills")}
            </h3>
            <MultiSelectField
              name={`educations.${index}.skills_education`}
              options={skillsData?.pages?.flatMap((page) => page.data) || []}
              label={tEducation("skillsLearned")}
              placeholder={tEducation("skillsPlaceholder")}
              useSearch={true}
              onSearch={setSearchNameSkill}
              hasMore={hasNextSkillsPage}
              onLoadMore={fetchNextSkillsPage}
              loading={isLoadingSkills || isFetchingNextSkillsPage}
              disabled={isConfirmed && !isEditing}
              className={flashClass('skills_education')}
              noBorder
              noShadow
              required
              bgStyle="bg-[#F9F9F9]"
            />
          </div>
        </div>

        {/* Reference Form */}
        <ReferenceForm
          baseName={`educations.${index}`}
          disabled={isConfirmed && !isEditing}
          missingSet={missingSet}
          flash={needsAttention && flashActive}
        />
        {refStarted && needsAttention && (
          <div className="px-4 -mt-2 text-xs text-red-600 font-medium flex flex-wrap gap-2 justify-center">
            {['reference_first_name', 'reference_last_name', 'reference_relationship', 'reference_email']
              .filter(f => missingSet.has(f))
              .map(f => {
                const label = f.replace('reference_', '').replace('_', ' ');
                return <span key={f} className="capitalize">{tCommon("missing")} {label}</span>;
              })}
          </div>
        )}
        {needsAttention && (
          <div className="pb-2 text-xs text-red-600 font-medium animate-pulse text-center">
            {tCommon("fillMissingFields")}
          </div>
        )}
        {!isConfirmed && !needsAttention && hasPartialData && localAttemptedConfirm > 0 && (
          <div className="bg-orange-50 border-l-4 border-orange-400 p-4 m-4 mb-0 rounded-r-md flex items-start gap-3">
            <LuInfo className="text-orange-500 mt-0.5" size={18} />
            <div>
              <p className="text-orange-700 text-sm font-medium">
                {tCommon("completeToConfirm")}
              </p>
              <p className="text-orange-600 text-xs mt-1">
                {tCommon("confirmToSave")}
              </p>
            </div>
          </div>
        )}
        {showConfirmReminder && !needsAttention && !hasPartialData && (
          <div
            className={`m-4 mb-2 rounded-lg border-2 transition-all duration-300 ${needsConfirmationHighlight
              ? 'border-red-500 bg-red-50/70 shadow-lg'
              : 'border-red-300 bg-red-50/50'
              }`}
          >
            <div className="px-4 py-3 flex items-start gap-3">
              <LuTriangleAlert className="text-red-500 mt-0.5" size={18} />
              <div>
                <p className="text-red-700 text-sm font-semibold">
                  {tCommon("confirmEducationToContinue")}
                </p>
                <p className="text-red-600 text-xs mt-1">
                  {tCommon("confirmBeforeForward")}
                </p>
              </div>
            </div>
          </div>
        )}

        <div className="flex justify-end p-4 pt-0 pb-6">
          {isConfirmed ? (
            <button
              type="button"
              onClick={handleEdit}
              className="w-full md:w-auto tracking-wide transition-colors duration-200 transform focus:outline-none focus:ring focus:ring-opacity-50 rounded-full px-7 py-2 bg-brand-500 hover:bg-brand-400 focus:bg-brand-400 focus:ring-brand-300 text-white cursor-pointer"
            >
              {tCommon("edit")}
            </button>
          ) : (
            <button
              type="button"
              onClick={handleConfirm}
              className={`w-full md:w-auto tracking-wide transition-colors duration-200 transform focus:outline-none focus:ring focus:ring-opacity-50 rounded-full px-7 py-2 bg-brand-500 hover:bg-brand-400 focus:bg-brand-400 focus:ring-brand-300 text-white cursor-pointer`}
            >
              {tCommon("confirm")}
            </button>
          )}
        </div>
      </div>

      <NoReferenceWarningModal
        isOpen={showNoReferenceWarning}
        onClose={handleWarningClose}
      />
    </div>
  );
};
